import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useAppSelector } from 'hooks/redux';

import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import LinkIcon from '@mui/icons-material/Link';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { scheduledTransactionsUtils } from 'app-components/flux/scheduled-transactions';
import {
  AutoAdjustAmountEnum,
  type ScheduledTransactionType,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { billPresentmentSelectors } from 'app-components/flux/bill-presentment';

import type { STFormikProps } from './types';

const useStyles = makeStyles()((theme) => ({
  button: {
    maxWidth: 5150,
    minWidth: 200,
    boxShadow: theme.shadows[3],
    transition: '300ms',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '&:hover': {
      cursor: 'pointer',
      boxShadow: theme.shadows[5],
      background: theme.palette.action.hover,
      backgroundColor: theme.palette.action.hover,
    },
  },
  iconColor: {
    color: theme.palette.icon.light,
    marginBottom: 'auto',
  },
  textSummary: {
    flex: 1,
    width: '100%',
    marginTop: 8,
    padding: 16,
    verticalAlign: 'middle',
    borderRadius: 12,
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.dark,
  },
  bold: {
    fontWeight: 500,
  },
}));

interface BillConnectButtonProps {
  onClick: () => void;
  formikProps: STFormikProps;
}

const BillConnectButton = (props: BillConnectButtonProps) => {
  const { onClick, formikProps } = props;
  const { classes } = useStyles();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (onClick && (e.key === 'Enter' || e.keyCode === 13)) onClick();
  };

  const { autoAdjustAmount, autoAdjustDueOn } = formikProps.values || {};

  const allDisabled = Boolean(
    (!autoAdjustAmount || autoAdjustAmount === AutoAdjustAmountEnum.none) && !autoAdjustDueOn,
  );

  const leftSide = scheduledTransactionsUtils.getDisplayNameFromAdjustAmount(autoAdjustAmount);

  const hasBVA = useAppSelector((state) =>
    billPresentmentSelectors.getHasBVA(state, formikProps.values as unknown as ScheduledTransactionType),
  );

  const connected = Boolean(formikProps.values?.presentmentAccountId || hasBVA);

  return (
    <ButtonBase
      id='BillConnectButton'
      aria-label='bill-connnect-button'
      data-testid='BillConnectButton'
      component={Paper}
      elevation={4}
      onKeyDown={handleKeyDown}
      onClick={onClick}
      className={classes.button}
    >
      <Box display='flex' justifyContent='space-between' alignItems='center' width='100%'>
        <LinkIcon className={classes.iconColor} />

        <Box display='flex' flexDirection='column' marginLeft='8px' marginRight='auto'>
          <Typography variant='bodyMedium'>Bill Connect</Typography>

          <Typography variant='captionRegular' color='textSecondary'>
            Automatically update reminders with your most recent bill details.
          </Typography>
        </Box>

        <Button variant='text' endIcon={<ChevronRightRoundedIcon />}>
          {connected ? 'Change' : 'Connect'}
        </Button>
      </Box>
      {connected && !allDisabled ? (
        <Typography variant='captionRegular' className={classes.textSummary}>
          {'Display my '}
          <span className={classes.bold}>{leftSide}</span>
          {leftSide && autoAdjustDueOn ? ' and ' : null}
          {autoAdjustDueOn ? <span className={classes.bold}>Due Date</span> : null}
          {' once the statement period closes.'}
        </Typography>
      ) : null}
    </ButtonBase>
  );
};

BillConnectButton.displayName = 'BillConnectButton';

export default BillConnectButton;
